import axiosInstance from "./axiosInstance";
import { User, ApiResponse, UserProperties } from "../types/userTypes";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "../utils/localStorage";
import { useQuery } from "@tanstack/react-query";

export const UserApi = {
    login: async (mobile: string, password: string): Promise<User | null> => {
        try {
            const response = await axiosInstance.post<ApiResponse<User>>("/api/auth/login", {
                mobile,
                password,
            });

            if (response.data.success) {
                // ✅ Store token and user data in localStorage
                setLocalStorage("token", response.data.token);
                setLocalStorage("user", response.data.user);
                return response.data.user;
            } else {
                throw new Error(response.data.message || "Login failed");
            }
        } catch (error: any) {
            console.error("Login Error:", error);
            throw new Error(error.response?.data?.message || "Login failed");
        }
    },
    getUserFromStorage: (): User | null => {
        return getLocalStorage("user");
    },

    logout: () => {
        removeLocalStorage("token");
        removeLocalStorage("user");
    },

    /** 🔹 Fetch User Properties with Dynamic Query Parameters */
    getUserProperties: async (queryParams: Record<string, boolean>): Promise<UserProperties> => {
        const queryString = new URLSearchParams(
            Object.entries(queryParams)
                .filter(([_, value]) => value) // ✅ Sirf `true` wale params bhejo
                .map(([key, _]) => [key, "true"])
        ).toString();

        const response = await axiosInstance.get<ApiResponse<UserProperties>>(
            `/api/user/properties?${queryString}`
        );

        if (!response.data.success) {
            throw new Error(response.data.message || "Failed to fetch user properties");
        }
        return response.data;
    },

    deleteProperty:async (propertyId: string): Promise<void> => {
        try {
            const response = await axiosInstance.delete<ApiResponse<void>>(`/api/user/property/${propertyId}`);
    
            if (!response.data.success) {
                throw new Error(response.data.message || "Failed to delete property");
            }
        } catch (error: any) {
            console.error("Delete Property Error:", error);
            throw new Error(error.response?.data?.message || "Failed to delete property");
        }
    },

    getInterestedUsers: async (propertyId: string): Promise<any> => {
        try {
            const response = await axiosInstance.get(`/api/user/property/interested-users/${propertyId}`);
            if (!response.data.success) throw new Error(response.data.message || "Failed to fetch users");
            return response.data;
        } catch (error: any) {
            console.error("Fetch Interested Users Error:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch users");
        }
    },
};




/** ✅ Hook: Get User Properties with Dynamic Query Params */
export const useUserProperties = (queryParams?: Record<string, boolean>) => {
    return useQuery({
        queryKey: ["userProperties", queryParams], // ✅ Query caching ke liye dynamic key
        queryFn: () => UserApi.getUserProperties(queryParams || {}),
        enabled: Object.values(queryParams).some((value) => value === true), // ✅ Run only if any param is `true`
    });
};

